import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import {
  ApiError,
  createTournamentYoutubeBroadcasts,
  getTournamentYoutubeBroadcasts,
  type TournamentYoutubeBroadcastsResponse,
  type YoutubeBroadcastResult,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';
import { formatLocal, formatVenueTime } from '../../lib/time';

interface TournamentStreamProps {
  tournamentId: number;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Route-owned tournament broadcast preparation. The API remains authoritative
 * for eligibility, mappings, table labels, and the durable broadcast state.
 */
export function TournamentStream({ tournamentId }: TournamentStreamProps) {
  const [status, setStatus] = useState<TournamentYoutubeBroadcastsResponse | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const activeTournamentId = useRef(tournamentId);
  const statusController = useRef<AbortController | null>(null);
  const createController = useRef<AbortController | null>(null);
  const createPending = useRef(false);
  const reconciliationRequired = useRef(false);
  const latestStatusRequest = useRef(0);

  const isCurrentTournament = useCallback(
    (id: number, requestId?: number) => (
      activeTournamentId.current === id
      && (requestId === undefined || latestStatusRequest.current === requestId)
    ),
    [],
  );

  const loadStatus = useCallback(async (id: number, preserveError = false) => {
    // A completion from a departed route must not abort or overwrite the
    // current route's request.
    if (activeTournamentId.current !== id) return;
    statusController.current?.abort();
    const controller = new AbortController();
    statusController.current = controller;
    const requestId = ++latestStatusRequest.current;
    setLoading(true);
    if (!preserveError) setError(null);
    try {
      const next = await getTournamentYoutubeBroadcasts(id, controller.signal);
      if (!controller.signal.aborted && isCurrentTournament(id, requestId)) {
        setStatus(next);
        setSelectedTableId((current) => (
          current !== null && next.table_options.some((option) => option.table_id === current)
            ? current
            : null
        ));
        if (reconciliationRequired.current) {
          reconciliationRequired.current = false;
          createPending.current = false;
          setCreating(false);
        }
      }
    } catch (caught) {
      if (!controller.signal.aborted && isCurrentTournament(id, requestId) && !preserveError) {
        setError(errorMessage(caught, 'Could not load YouTube broadcast preparation.'));
      }
    } finally {
      if (!controller.signal.aborted && isCurrentTournament(id, requestId)) setLoading(false);
      if (statusController.current === controller) statusController.current = null;
    }
  }, [isCurrentTournament]);

  useEffect(() => {
    activeTournamentId.current = tournamentId;
    setStatus(null);
    setSelectedTableId(null);
    setError(null);
    setCreating(false);
    createPending.current = false;
    reconciliationRequired.current = false;
    void loadStatus(tournamentId);

    return () => {
      if (activeTournamentId.current !== tournamentId) return;
      statusController.current?.abort();
      createController.current?.abort();
      latestStatusRequest.current += 1;
    };
  }, [loadStatus, tournamentId]);

  const createBroadcast = async () => {
    if (
      status === null
      || selectedTableId === null
      || !status.eligible
      || !status.integration.available
      || createPending.current
    ) return;

    const targetTournamentId = tournamentId;
    const targetTableId = selectedTableId;
    createPending.current = true;
    const controller = new AbortController();
    createController.current = controller;
    setCreating(true);
    setError(null);
    try {
      const next = await createTournamentYoutubeBroadcasts(
        targetTournamentId,
        { tableId: targetTableId },
        controller.signal,
      );
      if (!controller.signal.aborted && isCurrentTournament(targetTournamentId)) {
        setStatus(next);
        setSelectedTableId((current) => (
          current !== null && next.table_options.some((option) => option.table_id === current)
            ? current
            : null
        ));
      }
    } catch (caught) {
      if (!controller.signal.aborted && isCurrentTournament(targetTournamentId)) {
        setError(errorMessage(caught, 'Could not create YouTube broadcast.'));
        // A transport error can be ambiguous: the API may have committed a
        // durable result before the response was lost. Re-read it before the
        // next mutation while preserving the original retryable error.
        reconciliationRequired.current = true;
        await loadStatus(targetTournamentId, true);
      }
    } finally {
      if (createController.current === controller) createController.current = null;
      if (isCurrentTournament(targetTournamentId)) {
        if (!reconciliationRequired.current) {
          createPending.current = false;
          if (!controller.signal.aborted) setCreating(false);
        }
      }
    }
  };

  const canCreate = (
    status !== null
    && selectedTableId !== null
    && status.eligible
    && status.integration.available
    && !loading
    && !creating
  );

  return (
    <section aria-labelledby="tournament-youtube-streams-heading" style={panelStyle}>
      <h2 id="tournament-youtube-streams-heading" style={{ ...T.subheading, margin: 0 }}>
        Tournament YouTube broadcasts
      </h2>
      <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>
        Select one mapped tournament table to create or resume its camera broadcast.
      </p>

      {loading && <p style={{ ...T.ui, color: PS.textSec, margin: 0 }}>Loading YouTube broadcast preparation…</p>}
      {error && (
        <div role="alert" style={errorStyle}>
          {error}{' '}
          <button onClick={() => void loadStatus(tournamentId)} style={retryStyle}>Retry</button>
        </div>
      )}

      {status && (
        <>
          {!status.integration.available && (
            <p role="alert" style={{ ...T.ui, color: PS.warning, margin: 0 }}>
              YouTube integration is unavailable{status.integration.reason ? `: ${status.integration.reason}` : ''}.
            </p>
          )}
          {!status.eligible && (
            <p role="alert" style={{ ...T.ui, color: PS.warning, margin: 0 }}>
              This tournament is not eligible for new YouTube broadcasts.
            </p>
          )}
          {status.table_options.length === 0 && (
            <p role="alert" style={{ ...T.ui, color: PS.warning, margin: 0 }}>
              No mapped tournament tables are available for YouTube preparation.
            </p>
          )}

          {status.table_options.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 560 }}>
              <label htmlFor="youtube-table" style={{ ...T.label, color: PS.textSec }}>Table to broadcast</label>
              <select
                id="youtube-table"
                value={selectedTableId ?? ''}
                onChange={(event) => setSelectedTableId(event.target.value === '' ? null : Number(event.target.value))}
                style={selectStyle}
                disabled={creating}
              >
                <option value="">Choose a mapped table</option>
                {status.table_options.map((option) => (
                  <option key={option.table_id} value={option.table_id}>
                    {option.table_name} · {option.camera_name} (channel {option.camera_channel})
                  </option>
                ))}
              </select>
            </div>
          )}

          <Button onClick={() => void createBroadcast()} disabled={!canCreate}>
            {creating ? 'Creating YouTube broadcast…' : 'Create YouTube broadcast'}
          </Button>

          {status.results.length > 0 && (
            <div>
              <h3 style={{ ...T.subheading, margin: '8px 0' }}>YouTube broadcast results</h3>
              <ul style={resultListStyle}>
                {status.results.map((result) => (
                  <li key={result.camera_channel} style={resultRowStyle}>
                    <strong>{result.camera_name}</strong>
                    <span>{result.title}</span>
                    <span style={{ ...T.caption, color: resultStateColor(result) }}>{resultStateLabel(result)}</span>
                    {result.visibility && <span style={{ ...T.caption, color: PS.textSec }}>Visibility: {result.visibility}</span>}
                    <span style={{ ...T.caption, color: PS.textSec }}>{formatScheduledStart(result.scheduled_start)}</span>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {result.watch_url && <a href={result.watch_url} target="_blank" rel="noreferrer" style={linkStyle}>Watch on YouTube</a>}
                      {result.studio_url && <a href={result.studio_url} target="_blank" rel="noreferrer" style={linkStyle}>YouTube settings</a>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

const panelStyle = { display: 'flex', flexDirection: 'column' as const, gap: 12, background: PS.white, border: `1px solid ${PS.border}`, borderRadius: 4, padding: 16 };
const errorStyle = { ...T.ui, color: PS.errorText, background: PS.errorBg, border: `1px solid ${PS.errorBorder}`, borderRadius: 4, padding: '12px 16px' };
const retryStyle = { ...T.ui, color: PS.linkBlue, background: 'transparent', border: 0, cursor: 'pointer', padding: 0, textDecoration: 'underline' };
const selectStyle = { ...T.ui, color: PS.nearBlack, background: PS.white, border: `1px solid ${PS.border}`, borderRadius: 3, padding: '8px 10px' };
const resultListStyle = { display: 'flex', flexDirection: 'column' as const, gap: 8, margin: 0, padding: 0, listStyle: 'none' };
const resultRowStyle = { display: 'flex', flexDirection: 'column' as const, gap: 3, ...T.ui, background: PS.pageSecondary, border: `1px solid ${PS.borderLight}`, borderRadius: 3, padding: 12 };
const linkStyle = { ...T.ui, color: PS.linkBlue };

function formatScheduledStart(value: string): string {
  return `${formatLocal(value, 'yyyy-MM-dd')} ${formatVenueTime(value)}`;
}

function resultStateLabel(result: YoutubeBroadcastResult): string {
  if (result.state === 'complete') return 'Ready';
  if (result.state === 'needs_reconciliation') return `Needs reconciliation${result.error_code ? ` — ${result.error_code}` : ''}`;
  if (result.state === 'failed') return `Failed${result.error_code ? ` — ${result.error_code}` : ''}`;
  return `${result.state}${result.error_code ? ` — ${result.error_code}` : ''}`;
}

function resultStateColor(result: YoutubeBroadcastResult): string {
  if (result.state === 'complete') return PS.success;
  if (result.state === 'failed' || result.state === 'needs_reconciliation') return PS.warning;
  return PS.textSec;
}
